import type React from 'react'
import { useRef } from 'react'

import type { TransitionStatus } from 'react-transition-group'
import { Transition } from 'react-transition-group'
import { styled, css } from 'styled-components'

import { Button } from '@b-stock/fe-core/bstock-react'

const transitionStyles: {
  [key in TransitionStatus]: ReturnType<typeof css>
} = {
  entering: css`
    opacity: 0;
    transform: scaleY(0);
  `,
  entered: css`
    opacity: 1;
    transform: scaleY(1);
  `,
  exiting: css`
    opacity: 0;
    transform: scaleY(0);
  `,
  exited: css`
    opacity: 0;
    transform: scaleY(0);
  `,
  unmounted: css``,
}

const ButtonWithTransition = styled(Button)<{ $state: TransitionStatus }>`
  position: absolute;
  width: 100%;
  grid-area: btn;

  transition:
    opacity 0.15s ease-out,
    transform 0.1s ease-out;
  transform-origin: bottom;
  ${(o) => transitionStyles[o.$state]}
`

const containerTransitionStyles: {
  [key in TransitionStatus]: ReturnType<typeof css>
} = {
  entering: css`
    padding-bottom: 0;
  `,
  entered: css`
    padding-bottom: 2.75rem;
  `,
  exiting: css`
    padding-bottom: 0;
  `,
  exited: css`
    padding-bottom: 0;
  `,
  unmounted: css``,
}

const Content = styled.div`
  grid-area: content;
`
const Container = styled.div<{ $state: TransitionStatus }>`
  position: relative;
  display: grid;
  gap: 0.625rem;
  grid-template-columns: 100%;
  grid-template-rows: auto;
  grid-template-areas:
    'content'
    'btn';

  transition: all 150ms ease-in-out;

  ${(o) => containerTransitionStyles[o.$state]}
`

const WithDynamicButton = ({
  isButtonVisible: visible,
  onClick,
  isButtonDisabled: disabled,
  buttonContent,
  children,
}: {
  isButtonVisible: boolean
  isButtonDisabled?: boolean
  onClick: React.MouseEventHandler<HTMLButtonElement>
  buttonContent: React.ReactNode
  children: React.ReactNode
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  return (
    <Transition nodeRef={containerRef} in={visible} timeout={150}>
      {(state) => (
        <Container $state={state} ref={containerRef}>
          <Content>{children}</Content>

          <Transition
            nodeRef={buttonRef}
            in={visible}
            timeout={150}
            unmountOnExit
          >
            {(state) => (
              <ButtonWithTransition
                type="button"
                ref={buttonRef}
                $state={state}
                onClick={onClick}
                disabled={disabled}
                data-testid="show-results-button"
              >
                {buttonContent}
              </ButtonWithTransition>
            )}
          </Transition>
        </Container>
      )}
    </Transition>
  )
}

export default WithDynamicButton
